import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, Search, X } from "lucide-react";
import type { OrderStatus } from "@ticketnjoy/types";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { QueryError } from "@/components/query-error";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ORDER_PAGE_SIZE, useOrdersQuery, type OrderListParams } from "@/features/api";
import {
  ORDER_STATUS_LABELS,
  PAYMENT_PROVIDER_LABELS,
  dateTime,
  money,
  orderStatusVariant,
} from "@/lib/format";

const STATUS_FILTERS: OrderStatus[] = [
  "Paid",
  "Pending",
  "PartiallyRefunded",
  "Refunded",
  "Cancelled",
  "Expired",
];
const PROVIDER_FILTERS = ["Param", "Free", "Dev"];
const ALL = "all";

type TextFilterKey = Exclude<keyof OrderListParams, "page" | "problem">;
type FilterUpdate = Partial<Record<TextFilterKey, string>> & { problem?: boolean; page?: number };

/** Filtreler URL'de; organizatör ve etkinlik filtresi organizatör detayındaki bağlantıdan gelir. */
export function OrdersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawStatus = searchParams.get("status") as OrderStatus | null;
  const rawProvider = searchParams.get("provider") ?? "";
  const params: OrderListParams = {
    q: searchParams.get("q") ?? "",
    status: rawStatus && STATUS_FILTERS.includes(rawStatus) ? rawStatus : "",
    provider: PROVIDER_FILTERS.includes(rawProvider) ? rawProvider : "",
    from: searchParams.get("from") ?? "",
    to: searchParams.get("to") ?? "",
    organizerId: searchParams.get("organizerId") ?? "",
    eventId: searchParams.get("eventId") ?? "",
    problem: searchParams.get("problem") === "true",
    page: Math.max(1, Number(searchParams.get("page") ?? "1") || 1),
  };

  const [searchInput, setSearchInput] = useState(params.q);

  function update(next: FilterUpdate) {
    const merged = { ...params, page: 1, ...next };
    const nextParams = new URLSearchParams();
    for (const [key, value] of Object.entries(merged)) {
      if (!value || (key === "page" && value === 1)) continue;
      nextParams.set(key, String(value));
    }
    setSearchParams(nextParams, { replace: true });
  }

  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === params.q) return;
    const t = setTimeout(() => update({ q: trimmed }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sadece input değişince
  }, [searchInput]);

  const { data, isLoading, isFetching, error, refetch } = useOrdersQuery(params);
  const pageCount = data ? Math.max(1, Math.ceil(data.total / ORDER_PAGE_SIZE)) : 1;
  const first = data?.items[0];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Siparişler</h1>
        <p className="text-sm text-muted-foreground">
          {data ? `${data.total.toLocaleString("tr-TR")} kayıt · ` : ""}
          {params.organizerId ? "" : "Bütün organizatörler. "}
          Tarih, siparişin oluşturulduğu gün (Türkiye saati).
        </p>
      </div>

      <div className="space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Sipariş no, alıcı adı, e-posta, telefon veya bilet kodu"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          <Button
            variant={params.problem ? "destructive" : "outline"}
            aria-pressed={params.problem}
            onClick={() => update({ problem: !params.problem })}
          >
            <AlertTriangle />
            Sorunlu ödemeler
          </Button>
        </div>
        {params.problem && (
          <p className="text-sm text-muted-foreground">
            Bankanın "ödendi" dediği hâlde tamamlanamayan siparişler ile ödemesi başlatılıp dönüşü
            gelmeden süresi dolanlar (son 30 gün). Kapatılan sorunlar listeden çıkar.
          </p>
        )}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Select
            value={params.status || ALL}
            onValueChange={(v) => update({ status: v === ALL ? "" : v })}
          >
            <SelectTrigger aria-label="Durum">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tüm durumlar</SelectItem>
              {STATUS_FILTERS.map((s) => (
                <SelectItem key={s} value={s}>
                  {ORDER_STATUS_LABELS[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={params.provider || ALL}
            onValueChange={(v) => update({ provider: v === ALL ? "" : v })}
          >
            <SelectTrigger aria-label="Ödeme">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tüm ödemeler</SelectItem>
              {PROVIDER_FILTERS.map((p) => (
                <SelectItem key={p} value={p}>
                  {PAYMENT_PROVIDER_LABELS[p]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="w-18 shrink-0">Başlangıç</span>
            <Input
              type="date"
              value={params.from}
              max={params.to || undefined}
              onChange={(e) => update({ from: e.target.value })}
            />
          </label>
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="w-18 shrink-0">Bitiş</span>
            <Input
              type="date"
              value={params.to}
              min={params.from || undefined}
              onChange={(e) => update({ to: e.target.value })}
            />
          </label>
        </div>
        {(params.organizerId || params.eventId) && (
          <div className="flex flex-wrap gap-2">
            {params.organizerId && (
              <FilterChip
                label={`Organizatör: ${first?.organizerName ?? "seçili"}`}
                onClear={() => update({ organizerId: "", eventId: "" })}
              />
            )}
            {params.eventId && (
              <FilterChip
                label={`Etkinlik: ${first?.eventTitle ?? "seçili"}`}
                onClear={() => update({ eventId: "" })}
              />
            )}
          </div>
        )}
      </div>

      <Card className="py-0">
        <CardContent className="px-0">
          {error ? (
            <QueryError
              className="m-6"
              message="Liste yüklenemedi."
              onRetry={() => refetch()}
              retrying={isFetching}
            />
          ) : isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Yükleniyor...</p>
          ) : data && data.items.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">Kayıt bulunamadı.</p>
          ) : (
            <div className={isFetching ? "overflow-x-auto opacity-60" : "overflow-x-auto"}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="px-4 py-3 font-medium">Sipariş</th>
                    <th className="px-4 py-3 font-medium">Organizatör / etkinlik</th>
                    <th className="px-4 py-3 font-medium">Alıcı</th>
                    <th className="px-4 py-3 text-right font-medium">Tutar</th>
                    <th className="px-4 py-3 font-medium">Durum</th>
                    <th className="px-4 py-3 font-medium">Ödeme</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.items.map((o) => (
                    <tr key={o.orderNumber} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Link
                          to={`/orders/${encodeURIComponent(o.orderNumber)}`}
                          className="font-mono text-xs font-medium hover:underline"
                        >
                          {o.orderNumber}
                        </Link>
                        <div className="text-xs text-muted-foreground">{dateTime(o.createdAt)}</div>
                      </td>
                      <td className="max-w-64 px-4 py-3">
                        <Link
                          to={`/organizers/${o.organizerId}`}
                          className="font-medium hover:underline"
                        >
                          {o.organizerName}
                        </Link>
                        <div className="truncate text-xs text-muted-foreground">{o.eventTitle}</div>
                      </td>
                      <td className="max-w-64 px-4 py-3">
                        <div className="truncate">
                          {o.buyerFirstName} {o.buyerLastName}
                        </div>
                        <div className="truncate text-xs text-muted-foreground">{o.buyerEmail}</div>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap tabular-nums">
                        {money(o.total, o.currency)}
                        <div className="text-xs text-muted-foreground">{o.ticketCount} bilet</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          <Badge variant={orderStatusVariant(o.status)}>
                            {ORDER_STATUS_LABELS[o.status] ?? o.status}
                          </Badge>
                          {o.paymentAttention && <Badge variant="destructive">Sorunlu</Badge>}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                        {o.paymentProvider
                          ? (PAYMENT_PROVIDER_LABELS[o.paymentProvider] ?? o.paymentProvider)
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {pageCount > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button
            variant="outline"
            size="sm"
            disabled={params.page <= 1}
            onClick={() => update({ page: params.page - 1 })}
          >
            Önceki
          </Button>
          <span className="text-muted-foreground">
            {params.page} / {pageCount}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={params.page >= pageCount}
            onClick={() => update({ page: params.page + 1 })}
          >
            Sonraki
          </Button>
        </div>
      )}
    </div>
  );
}

function FilterChip({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-md border bg-background py-1 pr-1 pl-2.5 text-xs">
      {label}
      <button
        type="button"
        onClick={onClear}
        aria-label="Filtreyi kaldır"
        className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <X className="size-3.5" />
      </button>
    </span>
  );
}
